import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import { api } from '@/shared/api/http'
import type { CashFlowReportOut } from '@/shared/api/types'

/** Granularidade dos períodos do fluxo de caixa. */
export type CashFlowGroupBy = 'month' | 'day'

export const reportKeys = {
  all: () => ['reports'] as const,
  cashFlow: (p: CashFlowParams) => [...reportKeys.all(), 'cash-flow', p] as const,
} as const

export interface CashFlowParams {
  /** Início (inclusivo). Ausente → backend usa o 1º dia do mês de `to`. */
  readonly from?: string
  /** Fim (inclusivo). Ausente → backend usa o último dia do mês corrente. */
  readonly to?: string
  readonly group_by?: CashFlowGroupBy
  /** Projeção (agendamentos em aberto por vencimento) — NUNCA entra em net/saldo. */
  readonly include_projected?: boolean
  /**
   * Série BANCÁRIA (transações espelhadas do processor) — opt-in e ADITIVA:
   * sem a flag, o contrato é exatamente o de antes.
   *
   * Com ela, cada período ganha `bank_*` e o envelope ganha `bank`
   * (disponibilidade, motivo e truncamento). Nada disso entra em
   * `net`/`closing_balance`; e quando a origem não pode ser lida os campos vêm
   * OMITIDOS, nunca zerados — zero seria indistinguível de "não houve
   * movimento".
   */
  readonly include_bank?: boolean
}

/**
 * `GET /v1/reports/cash-flow` — visão 100% derivada, somente leitura.
 *
 * `from`/`to` são opcionais: o backend tem default (mês corrente) e ECOA o
 * período efetivo na resposta. Períodos sem movimento vêm zerados (série
 * completa, sem buracos). Com `include_projected=false` os campos
 * `projected_*` são OMITIDOS da resposta — nunca vêm null.
 */
export const cashFlowQueryOptions = (p: CashFlowParams = {}) =>
  queryOptions({
    queryKey: reportKeys.cashFlow(p),
    queryFn: ({ signal }) => api.get<CashFlowReportOut>('/v1/reports/cash-flow', { ...p }, signal),
    placeholderData: keepPreviousData,
    // Relatório financeiro: saldo velho é pior que um GET a mais.
    staleTime: 30_000,
  })
